// Full item editor (legacy ItemFormComponent / ServiceFormComponent /
// RecipeFormComponent parity). Used as `customForm` for the items, services
// and recipes resources. Steps:
//   standard: details → variants → locations → serials
//   service:  details
//   recipe:   details → portions (portion_items)
// Payload rules mirror ItemRequest + ItemTrait::saveItem:
//   - an existing photo URL is never sent back (only a new File)
//   - variation `meta` is an OBJECT ({ Size: 'M' }), `variants` = [{ name, options[] }]
//   - per-location stock only carries rack/cost/price and the low-stock alert
//     quantity (never quantity)
//   - serials are { number, till } rows
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add, ArrowLeft2, ArrowRight2, Shuffle, Trash } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, apiValidationErrors, createRecord, fetchRecord, fetchSelectOptions, updateRecord, type SelectOption } from '../../api/client';
import type { CrudCustomFormProps, CrudRecord } from '../../crud/types';
import { t, tLabel } from '../../i18n';
import { useSessionLocation } from '../../session/location';
import {
  CustomFieldsEditor,
  EditorSection,
  RemoteAutocomplete,
  asArray,
  asRecord,
  fieldError,
  idsOf,
  makeSku,
  numOr0,
  numOrNull,
  rowKey,
  useCustomFieldDefs,
  useLocations,
  validateCustomFields,
  type RemoteOption,
  type ValidationErrors
} from './shared';

export type ItemMode = 'standard' | 'service' | 'recipe';

type Num = number | '' | null;

// alert_quantity: low-stock threshold of the item at that location (items only, not variations).
type StockRow = { location_id: string; rack: string; cost: Num; price: Num; alert_quantity?: Num; dirty: boolean };

type VariationRow = {
  rowKey: string;
  id?: string;
  sku: string;
  code: string;
  cost: Num;
  price: Num;
  rack: string;
  weight: Num;
  dimensions: string;
  meta: Record<string, string>;
  stock: StockRow[];
};

type Variant = { name: string; options: string[] };

type RecipeLine = { rowKey: string; item: RemoteOption | null; cost: number };

type RecipePortion = { rowKey: string; id?: string; sku: string; name: string; cost: Num; price: Num; quantities: Record<string, Num> };

type Subunit = { id: string; name: string; cost: Num; price: Num };

type ItemForm = {
  id?: string;
  type: ItemMode;
  sku: string;
  name: string;
  alt_name: string;
  code: string;
  symbology: string;
  category_id: string;
  brand_id: string;
  supplier: RemoteOption | null;
  supplier_item_id: string;
  unit_id: string;
  sale_unit_id: string;
  purchase_unit_id: string;
  subunits: Subunit[];
  cost: Num;
  price: Num;
  taxes: string[];
  tax_included: boolean;
  changeable: boolean;
  min_price: Num;
  max_price: Num;
  expiry: boolean;
  max_discount: Num;
  hsn_number: string;
  weight: Num;
  dimensions: string;
  position: Num;
  rack: string;
  modifiers: string[];
  summary: string;
  details: string;
  hide_in_pos: boolean;
  hide_in_shop: boolean;
  photo: File | null;
  photoUrl: string;
  images: File[];
  video: string;
  extra_attributes: Record<string, unknown>;
  is_stock: boolean;
  stock: StockRow[];
  has_variants: boolean;
  variants: Variant[];
  variations: VariationRow[];
  has_serials: boolean;
  serials: Array<{ number: string; till: string }>;
  recipeLines: RecipeLine[];
  portions: RecipePortion[];
};

const symbologies = ['code128', 'code39', 'ean5', 'ean8', 'ean13', 'upca', 'upce'];

function emptyVariation(): VariationRow {
  return { rowKey: rowKey('var'), sku: makeSku(), code: '', cost: null, price: null, rack: '', weight: null, dimensions: '', meta: {}, stock: [] };
}

function emptyPortion(name: string, lines: RecipeLine[]): RecipePortion {
  return { rowKey: rowKey('portion'), sku: makeSku(), name, cost: null, price: null, quantities: Object.fromEntries(lines.map((line) => [line.rowKey, 1])) };
}

export function emptyForm(mode: ItemMode, prefill?: Record<string, unknown>): ItemForm {
  const firstLine: RecipeLine = { rowKey: rowKey('line'), item: null, cost: 0 };
  return {
    type: mode,
    sku: makeSku(),
    name: '',
    alt_name: '',
    code: '',
    symbology: 'code128',
    category_id: '',
    brand_id: '',
    supplier: null,
    supplier_item_id: '',
    unit_id: '',
    sale_unit_id: '',
    purchase_unit_id: '',
    subunits: [],
    cost: null,
    price: null,
    taxes: [],
    tax_included: false,
    changeable: false,
    min_price: null,
    max_price: null,
    expiry: false,
    max_discount: null,
    hsn_number: '',
    weight: null,
    dimensions: '',
    position: 99,
    rack: '',
    modifiers: [],
    summary: '',
    details: '',
    hide_in_pos: false,
    hide_in_shop: false,
    photo: null,
    photoUrl: '',
    images: [],
    video: '',
    extra_attributes: {},
    is_stock: false,
    stock: [],
    has_variants: false,
    variants: [],
    variations: [emptyVariation()],
    has_serials: false,
    serials: [{ number: '', till: '' }],
    recipeLines: [firstLine],
    portions: [emptyPortion('regular', [firstLine])],
    ...(prefill ? pickPrefill(prefill) : {})
  };
}

function pickPrefill(prefill: Record<string, unknown>): Partial<ItemForm> {
  const out: Partial<ItemForm> = {};
  (['name', 'code', 'alt_name', 'category_id'] as const).forEach((key) => {
    if (typeof prefill[key] === 'string') (out as Record<string, unknown>)[key] = prefill[key];
  });
  return out;
}

const s = (value: unknown) => (value == null ? '' : String(value));
const n = (value: unknown): Num => numOrNull(value);
const b = (value: unknown) => value === true || Number(value) === 1;

function stockRowsFrom(record: Record<string, unknown>, listRow: Record<string, unknown> | null): StockRow[] {
  const rows = [...asArray(listRow?.stock), ...asArray(record.location_stock)];
  const byLocation = new Map<string, StockRow>();
  rows.forEach((row) => {
    const location = s(row.location_id);
    if (!location) return;
    const known = byLocation.get(location);
    // undefined = threshold not loaded for this location: it is then never sent, so it cannot be cleared by accident.
    const alert = 'alert_quantity' in row ? n(row.alert_quantity) : undefined;
    if (known) {
      // The list row carries no threshold; take it from the full location stock.
      if (known.alert_quantity === undefined) known.alert_quantity = alert;
      return;
    }
    byLocation.set(location, { location_id: location, rack: s(row.rack), cost: n(row.cost), price: n(row.price), alert_quantity: alert, dirty: false });
  });
  return Array.from(byLocation.values());
}

export function formFromRecord(record: CrudRecord, listRow: Record<string, unknown> | null): ItemForm {
  const type = (['service', 'recipe'].includes(String(record.type)) ? record.type : 'standard') as ItemMode;
  const base = emptyForm(type);
  const categories = idsOf(record.categories);
  const unit = asRecord(record.unit);
  const subunitPrices = asArray(record.unit_price ?? record.unitPrice);
  const variants = asArray(record.variants).map((variant) => ({ name: s(variant.name), options: Array.isArray(variant.options) ? variant.options.map(String) : String(variant.options ?? '').split('|').filter(Boolean) }));
  const variations: VariationRow[] = asArray(record.variations).map((variation) => ({
    rowKey: rowKey('var'),
    id: s(variation.id),
    sku: s(variation.sku) || makeSku(),
    code: s(variation.code),
    cost: n(variation.cost),
    price: n(variation.price),
    rack: s(variation.rack),
    weight: n(variation.weight),
    dimensions: s(variation.dimensions),
    meta: Object.fromEntries(Object.entries(asRecord(variation.meta)).map(([key, value]) => [key, s(value)])),
    stock: asArray(variation.location_stock).map((row) => ({ location_id: s(row.location_id), rack: s(row.rack), cost: n(row.cost), price: n(row.price), dirty: false }))
  }));

  // Recipe portions → shared item rows with per-portion quantities.
  const lineByItem = new Map<string, RecipeLine>();
  const portionsRaw = asArray(record.portions);
  portionsRaw.forEach((portion) => {
    asArray(portion.portion_items).forEach((entry) => {
      const item = asRecord(entry.item);
      const id = s(entry.item_id ?? item.id);
      if (!id || lineByItem.has(id)) return;
      lineByItem.set(id, { rowKey: rowKey('line'), item: { value: id, label: item.name ? `${item.name}${item.code ? ` (${item.code})` : ''}` : id, raw: item }, cost: numOr0(item.cost) });
    });
  });
  const recipeLines = Array.from(lineByItem.values());
  const portions: RecipePortion[] = portionsRaw.map((portion) => {
    const quantities: Record<string, Num> = {};
    asArray(portion.portion_items).forEach((entry) => {
      const line = lineByItem.get(s(entry.item_id ?? asRecord(entry.item).id));
      if (line) quantities[line.rowKey] = n(entry.quantity);
    });
    return { rowKey: rowKey('portion'), id: s(portion.id), sku: s(portion.sku) || makeSku(), name: s(portion.name) || 'regular', cost: n(portion.cost), price: n(portion.price), quantities };
  });
  const serials = asArray(record.serials)
    .filter((serial) => serial.purchase_id == null)
    .map((serial) => ({ number: s(serial.number), till: '' }));
  const supplier = asRecord(record.supplier);

  return {
    ...base,
    id: s(record.id),
    type,
    sku: s(record.sku) || makeSku(),
    name: s(record.name),
    alt_name: s(record.alt_name),
    code: s(record.code),
    symbology: s(record.symbology) || 'code128',
    category_id: s(record.category_id ?? categories[0]),
    brand_id: s(record.brand_id),
    supplier: record.supplier_id ? { value: s(record.supplier_id), label: s(supplier.name ?? supplier.company ?? record.supplier_id) } : null,
    supplier_item_id: s(record.supplier_item_id),
    unit_id: s(record.unit_id),
    sale_unit_id: s(record.sale_unit_id),
    purchase_unit_id: s(record.purchase_unit_id),
    subunits: asArray(unit.subunits).map((sub) => {
      const price = subunitPrices.find((entry) => s(entry.unit_id) === s(sub.id));
      return { id: s(sub.id), name: s(sub.name), cost: n(price?.cost), price: n(price?.price) };
    }),
    cost: n(record.cost),
    price: n(record.price),
    taxes: idsOf(record.taxes).map(String),
    tax_included: b(record.tax_included),
    changeable: b(record.changeable),
    min_price: n(record.min_price),
    max_price: n(record.max_price),
    expiry: b(record.expiry),
    max_discount: n(record.max_discount),
    hsn_number: s(record.hsn_number),
    weight: n(record.weight),
    dimensions: s(record.dimensions),
    position: n(record.position),
    rack: s(record.rack),
    modifiers: idsOf(record.modifiers).map(String),
    summary: s(record.summary),
    details: s(record.details),
    hide_in_pos: b(record.hide_in_pos),
    hide_in_shop: b(record.hide_in_shop),
    photo: null,
    photoUrl: s(record.photo),
    images: [],
    video: s(record.video),
    extra_attributes: asRecord(record.extra_attributes),
    is_stock: b(record.is_stock),
    stock: stockRowsFrom(record, listRow),
    has_variants: b(record.has_variants),
    variants,
    variations: variations.length ? variations : [emptyVariation()],
    has_serials: b(record.has_serials),
    serials: serials.length ? [...serials, { number: '', till: '' }] : [{ number: '', till: '' }],
    recipeLines: recipeLines.length ? recipeLines : base.recipeLines,
    portions: portions.length ? portions : base.portions
  };
}

export function recipePortionCost(portion: RecipePortion, lines: RecipeLine[]) {
  return lines.reduce((sum, line) => sum + (line.item ? line.cost * numOr0(portion.quantities[line.rowKey]) : 0), 0);
}

function regularPortion(portions: RecipePortion[]) {
  return portions.find((portion) => portion.name.trim().toLowerCase() === 'regular') ?? portions[0];
}

// Only rows the user touched are sent (never `quantity`): untouched rows for
// other locations may not be loaded and must not overwrite stored values.
// `cost` is always present because ItemTrait::setVariations reads it directly.
// Sending rack/cost/price back with a changed alert keeps them as they are. An
// empty alert clears it; a threshold that was never loaded (undefined) is not sent.
function stockPayload(rows: StockRow[], withAlert = false) {
  return rows
    .filter((row) => row.dirty)
    .map((row) => ({
      location_id: row.location_id,
      rack: row.rack,
      cost: numOrNull(row.cost),
      price: numOrNull(row.price),
      ...(withAlert && row.alert_quantity !== undefined ? { alert_quantity: numOrNull(row.alert_quantity) } : {})
    }));
}

export function buildItemPayload(form: ItemForm): Record<string, unknown> {
  const mode = form.type;
  const payload: Record<string, unknown> = {
    // ItemRequest's unique rules (code, sku, supplier_item_id) ignore the row whose id is in the payload.
    ...(form.id ? { id: form.id } : {}),
    type: mode,
    sku: form.sku,
    name: form.name.trim(),
    alt_name: form.alt_name,
    code: form.code.trim(),
    symbology: form.symbology,
    category_id: form.category_id,
    brand_id: form.brand_id || null,
    supplier_id: form.supplier?.value ?? null,
    supplier_item_id: form.supplier ? form.supplier_item_id || null : null,
    unit_id: form.unit_id || null,
    sale_unit_id: form.unit_id ? form.sale_unit_id || null : null,
    purchase_unit_id: form.unit_id ? form.purchase_unit_id || null : null,
    cost: numOr0(form.cost),
    price: numOr0(form.price),
    taxes: form.taxes,
    tax_included: form.tax_included ? 1 : 0,
    changeable: form.changeable ? 1 : 0,
    min_price: form.changeable ? numOrNull(form.min_price) : null,
    max_price: form.changeable ? numOrNull(form.max_price) : null,
    expiry: form.expiry ? 1 : 0,
    max_discount: numOrNull(form.max_discount),
    hsn_number: form.hsn_number || null,
    weight: numOrNull(form.weight),
    dimensions: form.dimensions || null,
    position: numOrNull(form.position),
    rack: form.rack || null,
    modifiers: form.modifiers,
    summary: form.summary || null,
    details: form.details || null,
    hide_in_pos: form.hide_in_pos ? 1 : 0,
    hide_in_shop: form.hide_in_shop ? 1 : 0,
    video: form.video.trim() || null,
    extra_attributes: form.extra_attributes,
    is_stock: 0,
    has_variants: 0,
    has_serials: 0
  };
  if (form.photo) payload.photo = form.photo;
  if (form.images.length) payload.images = form.images;
  if (form.unit_id && form.subunits.length) {
    payload.unit = { id: form.unit_id, subunits: form.subunits.map((sub) => ({ id: sub.id, cost: numOrNull(sub.cost), price: numOrNull(sub.price) })) };
  }

  if (mode === 'standard') {
    payload.is_stock = form.is_stock ? 1 : 0;
    const stock = stockPayload(form.stock, true);
    if (stock.length) payload.stock = stock;
    if (form.has_variants) {
      payload.has_variants = 1;
      payload.variants = form.variants.map((variant) => ({ name: variant.name, options: variant.options }));
      payload.variations = form.variations
        .filter((variation) => Object.values(variation.meta).some((value) => value !== ''))
        .map((variation) => {
          const stockRows = stockPayload(variation.stock);
          return {
            // New variations need a quantity (VariationObserver writes it to stock_trails);
            // existing ones never resend it.
            ...(variation.id ? { id: variation.id } : { quantity: 0 }),
            sku: variation.sku,
            code: variation.code || null,
            cost: numOrNull(variation.cost),
            price: numOrNull(variation.price),
            rack: variation.rack || null,
            weight: numOrNull(variation.weight),
            dimensions: variation.dimensions || null,
            meta: Object.fromEntries(Object.entries(variation.meta).filter(([, value]) => value !== '')),
            ...(stockRows.length ? { stock: stockRows } : {})
          };
        });
    }
    if (form.has_serials) {
      payload.has_serials = 1;
      const serials = form.serials.filter((serial) => serial.number.trim()).map((serial) => ({ number: serial.number.trim(), till: serial.till.trim() || null }));
      if (serials.length) payload.serials = serials;
    }
  }

  if (mode === 'recipe') {
    const portions = form.portions.map((portion) => ({
      ...(portion.id ? { id: portion.id } : {}),
      sku: portion.sku,
      name: portion.name.trim(),
      cost: numOr0(portion.cost),
      price: numOr0(portion.price),
      portion_items: form.recipeLines
        .filter((line) => line.item && numOr0(portion.quantities[line.rowKey]) > 0)
        .map((line) => ({ item_id: line.item!.value, quantity: numOr0(portion.quantities[line.rowKey]) }))
    }));
    payload.portions = portions;
    const regular = regularPortion(form.portions);
    payload.cost = numOr0(regular?.cost);
    payload.price = numOr0(regular?.price);
  }
  return payload;
}

/** Multipart requests drop null values, so send '' (Laravel turns it into null). */
function nullsToEmpty<T>(value: T): T {
  if (value === null) return '' as unknown as T;
  if (typeof File !== 'undefined' && value instanceof Blob) return value;
  if (Array.isArray(value)) return value.map((entry) => nullsToEmpty(entry)) as unknown as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, entry]) => [key, nullsToEmpty(entry)])) as T;
  }
  return value;
}

function validateDetails(form: ItemForm, customErrors: ValidationErrors): ValidationErrors {
  const errors: ValidationErrors = { ...customErrors };
  if (!form.name.trim()) errors.name = [t('catalog.validation.required', { field: tLabel('Name') })];
  if (!/^[a-zA-Z0-9_-]{2,25}$/.test(form.code.trim())) errors.code = [t('catalog.validation.alphaDash')];
  if (!form.category_id) errors.category_id = [t('catalog.validation.required', { field: tLabel('Category') })];
  if (!form.symbology) errors.symbology = [t('catalog.validation.required', { field: t('catalog.symbology') })];
  if (form.type !== 'recipe') {
    if (form.cost === '' || form.cost == null) errors.cost = [t('catalog.validation.required', { field: tLabel('Cost') })];
    if (form.price === '' || form.price == null) errors.price = [t('catalog.validation.required', { field: tLabel('Price') })];
  }
  if (form.changeable && form.min_price != null && form.max_price != null && form.min_price !== '' && form.max_price !== '' && Number(form.min_price) > Number(form.max_price)) {
    errors.max_price = [t('catalog.validation.maxAboveMin')];
  }
  if (form.video.trim() && !/^https?:\/\//i.test(form.video.trim())) errors.video = [t('catalog.validation.url')];
  return errors;
}

function validateVariants(form: ItemForm): ValidationErrors {
  if (form.type !== 'standard' || !form.has_variants) return {};
  const errors: ValidationErrors = {};
  if (!form.variants.length) errors.variants = [t('catalog.validation.variantsRequired')];
  else if (!form.variations.some((variation) => Object.values(variation.meta).some(Boolean))) errors.variants = [t('catalog.validation.variationsRequired')];
  return errors;
}

function validatePortions(form: ItemForm): ValidationErrors {
  if (form.type !== 'recipe') return {};
  const errors: ValidationErrors = {};
  if (!form.recipeLines.some((line) => line.item)) errors.portions = [t('catalog.validation.recipeItems')];
  form.portions.forEach((portion, index) => {
    if (!portion.name.trim()) errors[`portions.${index}.name`] = [t('catalog.validation.required', { field: tLabel('Name') })];
    // ItemTrait::setPortions silently skips a portion whose cost is empty/zero.
    if (!(numOr0(portion.cost) > 0)) errors[`portions.${index}.cost`] = [t('catalog.validation.positive', { field: tLabel('Cost') })];
    if (portion.price === '' || portion.price == null) errors[`portions.${index}.price`] = [t('catalog.validation.required', { field: tLabel('Price') })];
    const missing = form.recipeLines.some((line) => line.item && !(numOr0(portion.quantities[line.rowKey]) > 0));
    if (missing) errors[`portions.${index}.items`] = [t('catalog.validation.recipeQuantity')];
  });
  if (!form.portions.some((portion) => portion.name.trim().toLowerCase() === 'regular')) errors.portions = [t('catalog.validation.regularPortion')];
  return errors;
}

function stepsFor(mode: ItemMode) {
  if (mode === 'service') return ['details'];
  if (mode === 'recipe') return ['details', 'portions'];
  return ['details', 'variants', 'locations', 'serials'];
}

export function makeItemEditor(mode: ItemMode) {
  return function ItemEditorForMode(props: CrudCustomFormProps) {
    return <ItemEditor {...props} mode={mode} />;
  };
}

export function ItemEditor({ recordId, prefill, onClose, onSaved, mode: defaultMode }: CrudCustomFormProps & { mode: ItemMode }) {
  const editing = recordId != null;
  const [form, setForm] = useState<ItemForm>(() => emptyForm(defaultMode, prefill));
  const [loading, setLoading] = useState(editing);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unsupportedType, setUnsupportedType] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState<Record<string, SelectOption[]>>({});
  const [units, setUnits] = useState<Array<Record<string, unknown>>>([]);
  const locations = useLocations();
  const customDefs = useCustomFieldDefs('items');
  const steps = stepsFor(form.type);

  useEffect(() => {
    void Promise.all([
      fetchSelectOptions('all_categories/search').catch(() => []),
      fetchSelectOptions('taxes/search').catch(() => []),
      fetchSelectOptions('brands/search').catch(() => []),
      fetchSelectOptions('modifiers/search').catch(() => [])
    ]).then(([categories, taxes, brands, modifiers]) => setOptions({ categories, taxes, brands, modifiers }));
    adminApi
      .get('units/search')
      .then((response) => setUnits(Array.isArray(response.data) ? response.data : []))
      .catch(() => setUnits([]));
  }, []);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [record, list] = await Promise.all([
          fetchRecord<CrudRecord>('items', recordId!),
          adminApi.get('items', { params: { query: `id=${recordId}`, byColumn: 0, limit: 1, page: 1, orderBy: "code asc" } }).then((response) => (response.data?.data?.[0] as Record<string, unknown>) ?? null).catch(() => null)
        ]);
        if (cancelled) return;
        if (!['standard', 'service', 'recipe'].includes(String(record.type ?? 'standard'))) {
          setUnsupportedType(String(record.type));
        }
        setForm(formFromRecord(record, list));
      } catch (err) {
        if (!cancelled) setLoadError(apiErrorMessage(err, t('common.unableToLoad', { name: tLabel('Item') })));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, recordId]);

  // Make sure every location has a stock row (editable cost/price/rack).
  useEffect(() => {
    if (!locations.length) return;
    setForm((current) => {
      const ensure = (rows: StockRow[]) => locations.map((location) => rows.find((row) => row.location_id === location.value) ?? { location_id: location.value, rack: '', cost: null, price: null, dirty: false });
      return { ...current, stock: ensure(current.stock), variations: current.variations.map((variation) => ({ ...variation, stock: ensure(variation.stock) })) };
    });
  }, [locations, loading]);

  const update = <K extends keyof ItemForm>(key: K, value: ItemForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  };

  const selectUnit = (unitId: string) => {
    const unit = units.find((entry) => String(entry.id) === unitId);
    setForm((current) => ({
      ...current,
      unit_id: unitId,
      sale_unit_id: '',
      purchase_unit_id: '',
      subunits: asArray(unit?.subunits).map((sub) => ({ id: s(sub.id), name: s(sub.name), cost: null, price: null }))
    }));
  };

  const goTo = (target: number) => {
    if (target > step && step === 0) {
      const detailErrors = validateDetails(form, validateCustomFields(customDefs, form.extra_attributes));
      setErrors(detailErrors);
      if (Object.keys(detailErrors).length) {
        setNotice(t('common.fixHighlighted'));
        return;
      }
    }
    if (target > step && steps[step] === 'variants') {
      const variantErrors = validateVariants(form);
      setErrors(variantErrors);
      if (Object.keys(variantErrors).length) return;
    }
    setNotice(null);
    setStep(target);
  };

  const save = async () => {
    const detailErrors = validateDetails(form, validateCustomFields(customDefs, form.extra_attributes));
    const allErrors = { ...detailErrors, ...validateVariants(form), ...validatePortions(form) };
    setErrors(allErrors);
    if (Object.keys(allErrors).length) {
      setNotice(t('common.fixHighlighted'));
      if (Object.keys(detailErrors).length) setStep(0);
      else if (allErrors.variants) setStep(steps.indexOf('variants'));
      else setStep(steps.indexOf('portions'));
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      let payload = buildItemPayload(form);
      const multipart = Boolean(form.photo || form.images.length);
      if (multipart) payload = nullsToEmpty(payload);
      const response = editing ? await updateRecord<CrudRecord>('items', recordId!, payload) : await createRecord<CrudRecord>('items', payload);
      if (response && response.success === false) {
        setNotice(response.message ?? t('common.saveFailed'));
        return;
      }
      onSaved(response?.data);
    } catch (err) {
      const validation = apiValidationErrors(err);
      setErrors(validation);
      const keys = Object.keys(validation);
      setNotice(keys.length ? Object.values(validation).flat().slice(0, 3).join(' ') : apiErrorMessage(err, t('common.saveFailed')));
      if (keys.some((key) => !key.startsWith('portions') && !key.startsWith('variations') && !key.startsWith('serials'))) setStep(0);
    } finally {
      setSaving(false);
    }
  };

  const title = editing ? t('catalog.item.editTitle', { type: t(`catalog.type.${form.type}`) }) : t('catalog.item.addTitle', { type: t(`catalog.type.${form.type}`) });

  return (
    <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="lg">
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography component="span" variant="h5" fontWeight={700}>{title}</Typography>
          {steps.length > 1 ? (
            <Stack direction="row" spacing={1}>
              {steps.map((key, index) => (
                <Chip key={key} label={`${index + 1}. ${t(`catalog.step.${key}`)}`} color={index === step ? 'primary' : 'default'} variant={index === step ? 'filled' : 'outlined'} onClick={() => goTo(index)} />
              ))}
            </Stack>
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
        {loadError ? <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert> : null}
        {unsupportedType ? <Alert severity="warning" sx={{ mb: 2 }}>{t('catalog.item.useComboPage')}</Alert> : null}
        {notice ? <Alert severity="error" sx={{ mb: 2 }}>{notice}</Alert> : null}
        {!loading && !loadError && !unsupportedType ? (
          <Stack spacing={2}>
            {steps[step] === 'details' ? (
              <DetailsStep form={form} errors={errors} options={options} units={units} update={update} selectUnit={selectUnit} customDefs={customDefs} />
            ) : null}
            {steps[step] === 'variants' ? <VariantsStep form={form} errors={errors} setForm={setForm} /> : null}
            {steps[step] === 'locations' ? <LocationsStep form={form} locations={locations} setForm={setForm} /> : null}
            {steps[step] === 'serials' ? <SerialsStep form={form} errors={errors} setForm={setForm} /> : null}
            {steps[step] === 'portions' ? <PortionsStep form={form} errors={errors} setForm={setForm} /> : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box>
          {step > 0 ? <Button startIcon={<ArrowLeft2 size={16} />} onClick={() => setStep(step - 1)} disabled={saving}>{t('action.back')}</Button> : null}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button color="inherit" onClick={onClose} disabled={saving}>{t('action.cancel')}</Button>
          {step < steps.length - 1 ? (
            <Button variant="outlined" endIcon={<ArrowRight2 size={16} />} onClick={() => goTo(step + 1)} disabled={saving || loading}>{t('action.next')}</Button>
          ) : null}
          <Button variant="contained" onClick={() => void save()} disabled={saving || loading || Boolean(loadError) || Boolean(unsupportedType)}>
            {saving ? t('action.saving') : t('action.save')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

type UpdateFn = <K extends keyof ItemForm>(key: K, value: ItemForm[K]) => void;

function numberInput(value: Num) {
  return value == null ? '' : value;
}

function parseNum(raw: string): Num {
  return raw === '' ? '' : Number(raw);
}

function Grid2({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>{children}</Box>;
}

function SelectField({ label, value, options, onChange, error, required, allowEmpty = true }: { label: string; value: string; options: SelectOption[]; onChange: (value: string) => void; error?: string; required?: boolean; allowEmpty?: boolean }) {
  return (
    <TextField select size="small" fullWidth label={label} value={value} required={required} error={Boolean(error)} helperText={error} onChange={(event) => onChange(event.target.value)}>
      {allowEmpty ? <MenuItem value="">{t('common.none')}</MenuItem> : null}
      {options.map((option) => <MenuItem key={String(option.value)} value={String(option.value)}>{option.label}</MenuItem>)}
    </TextField>
  );
}

function MultiSelectField({ label, value, options, onChange }: { label: string; value: string[]; options: SelectOption[]; onChange: (value: string[]) => void }) {
  return (
    <TextField
      select
      size="small"
      fullWidth
      label={label}
      value={value}
      SelectProps={{ multiple: true, renderValue: (selected) => (selected as string[]).map((id) => options.find((option) => String(option.value) === id)?.label ?? id).join(', ') }}
      onChange={(event) => onChange(typeof event.target.value === 'string' ? event.target.value.split(',') : (event.target.value as string[]))}
    >
      {options.map((option) => (
        <MenuItem key={String(option.value)} value={String(option.value)}>
          <Checkbox size="small" checked={value.includes(String(option.value))} />
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function DetailsStep({
  form,
  errors,
  options,
  units,
  update,
  selectUnit,
  customDefs
}: {
  form: ItemForm;
  errors: ValidationErrors;
  options: Record<string, SelectOption[]>;
  units: Array<Record<string, unknown>>;
  update: UpdateFn;
  selectUnit: (id: string) => void;
  customDefs: ReturnType<typeof useCustomFieldDefs>;
}) {
  const unitOptions = units.map((unit) => ({ value: String(unit.id), label: String(unit.name ?? unit.code ?? unit.id) }));
  const selectedUnit = units.find((unit) => String(unit.id) === form.unit_id);
  const unitChoices = selectedUnit ? [{ value: String(selectedUnit.id), label: String(selectedUnit.name) }, ...asArray(selectedUnit.subunits).map((sub) => ({ value: String(sub.id), label: String(sub.name) }))] : [];
  const isStandard = form.type === 'standard';
  const isRecipe = form.type === 'recipe';
  const err = (key: string) => fieldError(errors, key);

  return (
    <Stack spacing={2}>
      <EditorSection title={t('catalog.section.identity')}>
        <Grid2>
          <TextField size="small" label={t('catalog.sku')} value={form.sku} InputProps={{ readOnly: true }} helperText={t('catalog.generated')} error={Boolean(err('sku'))} />
          <TextField size="small" label={tLabel('Name')} required value={form.name} onChange={(e) => update('name', e.target.value)} error={Boolean(err('name'))} helperText={err('name')} />
          <TextField size="small" label={t('catalog.altName')} value={form.alt_name} onChange={(e) => update('alt_name', e.target.value)} />
          <TextField size="small" label={tLabel('Code')} required value={form.code} onChange={(e) => update('code', e.target.value)} error={Boolean(err('code'))} helperText={err('code') || t('catalog.codeTip')} />
          <SelectField label={t('catalog.symbology')} required allowEmpty={false} value={form.symbology} options={symbologies.map((value) => ({ value, label: value }))} onChange={(value) => update('symbology', value)} error={err('symbology')} />
          <SelectField label={tLabel('Category')} required value={form.category_id} options={options.categories ?? []} onChange={(value) => update('category_id', value)} error={err('category_id')} />
          {isStandard ? <SelectField label={tLabel('Brand')} value={form.brand_id} options={options.brands ?? []} onChange={(value) => update('brand_id', value)} error={err('brand_id')} /> : null}
          <MultiSelectField label={t('nav.modifiers')} value={form.modifiers} options={options.modifiers ?? []} onChange={(value) => update('modifiers', value)} />
          <TextField size="small" label={t('catalog.hsnNumber')} value={form.hsn_number} onChange={(e) => update('hsn_number', e.target.value)} error={Boolean(err('hsn_number'))} helperText={err('hsn_number')} />
          {isStandard ? <TextField size="small" type="number" label={t('catalog.position')} value={numberInput(form.position)} onChange={(e) => update('position', parseNum(e.target.value))} error={Boolean(err('position'))} helperText={err('position')} /> : null}
        </Grid2>
      </EditorSection>

      <EditorSection title={t('catalog.section.pricing')}>
        <Grid2>
          {!isRecipe ? (
            <>
              <TextField size="small" type="number" label={tLabel('Cost')} required value={numberInput(form.cost)} onChange={(e) => update('cost', parseNum(e.target.value))} error={Boolean(err('cost'))} helperText={err('cost')} />
              <TextField size="small" type="number" label={tLabel('Price')} required value={numberInput(form.price)} onChange={(e) => update('price', parseNum(e.target.value))} error={Boolean(err('price'))} helperText={err('price')} />
            </>
          ) : null}
          <MultiSelectField label={t('nav.taxes')} value={form.taxes} options={options.taxes ?? []} onChange={(value) => update('taxes', value)} />
          <TextField size="small" type="number" label={t('catalog.maxDiscount')} value={numberInput(form.max_discount)} onChange={(e) => update('max_discount', parseNum(e.target.value))} helperText={err('max_discount') || t('catalog.maxDiscountTip')} error={Boolean(err('max_discount'))} />
        </Grid2>
        <Stack direction="row" flexWrap="wrap" sx={{ mt: 1 }}>
          {!isRecipe ? <FormControlLabel control={<Checkbox checked={form.tax_included} onChange={(e) => update('tax_included', e.target.checked)} />} label={t('catalog.taxIncluded')} /> : null}
          <FormControlLabel control={<Checkbox checked={form.changeable} onChange={(e) => update('changeable', e.target.checked)} />} label={t('catalog.priceChangeable')} />
          {isStandard ? <FormControlLabel control={<Checkbox checked={form.expiry} onChange={(e) => update('expiry', e.target.checked)} />} label={t('catalog.hasExpiry')} /> : null}
        </Stack>
        {form.changeable ? (
          <Grid2>
            <TextField size="small" type="number" label={t('catalog.minPrice')} value={numberInput(form.min_price)} onChange={(e) => update('min_price', parseNum(e.target.value))} error={Boolean(err('min_price'))} helperText={err('min_price')} />
            <TextField size="small" type="number" label={t('catalog.maxPrice')} value={numberInput(form.max_price)} onChange={(e) => update('max_price', parseNum(e.target.value))} error={Boolean(err('max_price'))} helperText={err('max_price')} />
          </Grid2>
        ) : null}
      </EditorSection>

      {isStandard ? (
        <EditorSection title={t('catalog.section.inventory')}>
          <Grid2>
            <RemoteAutocomplete
              endpoint="suppliers/search"
              label={tLabel('Supplier')}
              placeholder={t('catalog.searchSupplier')}
              value={form.supplier}
              onChange={(option) => update('supplier', option)}
              error={err('supplier_id')}
            />
            {form.supplier ? (
              <TextField size="small" label={t('catalog.supplierItemId')} value={form.supplier_item_id} onChange={(e) => update('supplier_item_id', e.target.value)} error={Boolean(err('supplier_item_id'))} helperText={err('supplier_item_id')} />
            ) : <Box />}
            <SelectField label={tLabel('Unit')} value={form.unit_id} options={unitOptions} onChange={selectUnit} error={err('unit_id')} />
            <TextField size="small" label={t('catalog.rackLocation')} value={form.rack} onChange={(e) => update('rack', e.target.value)} />
            {unitChoices.length > 1 ? (
              <>
                <SelectField label={t('catalog.saleUnit')} value={form.sale_unit_id} options={unitChoices} onChange={(value) => update('sale_unit_id', value)} />
                <SelectField label={t('catalog.purchaseUnit')} value={form.purchase_unit_id} options={unitChoices} onChange={(value) => update('purchase_unit_id', value)} />
              </>
            ) : null}
            <TextField size="small" type="number" label={t('catalog.weight')} value={numberInput(form.weight)} onChange={(e) => update('weight', parseNum(e.target.value))} />
            <TextField size="small" label={t('catalog.dimensions')} placeholder={t('catalog.dimensionsTip')} value={form.dimensions} onChange={(e) => update('dimensions', e.target.value)} />
          </Grid2>
          {form.subunits.length ? (
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('catalog.subunit')}</TableCell>
                  <TableCell width={180}>{tLabel('Cost')}</TableCell>
                  <TableCell width={180}>{tLabel('Price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.subunits.map((sub, index) => (
                  <TableRow key={sub.id}>
                    <TableCell>{sub.name}</TableCell>
                    <TableCell><TextField size="small" type="number" value={numberInput(sub.cost)} onChange={(e) => update('subunits', form.subunits.map((entry, i) => (i === index ? { ...entry, cost: parseNum(e.target.value) } : entry)))} /></TableCell>
                    <TableCell><TextField size="small" type="number" value={numberInput(sub.price)} onChange={(e) => update('subunits', form.subunits.map((entry, i) => (i === index ? { ...entry, price: parseNum(e.target.value) } : entry)))} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </EditorSection>
      ) : null}

      <CustomFieldsEditor defs={customDefs} values={form.extra_attributes} errors={errors} onChange={(slug, value) => update('extra_attributes', { ...form.extra_attributes, [slug]: value })} />

      <EditorSection title={t('catalog.section.media')}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            {form.photo || form.photoUrl ? (
              <Box component="img" src={form.photo ? URL.createObjectURL(form.photo) : form.photoUrl} alt="" sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
            ) : null}
            <Button variant="outlined" component="label">
              {form.photo ? form.photo.name : t('catalog.selectPhoto')}
              <input hidden type="file" accept="image/*" onChange={(e) => update('photo', e.target.files?.[0] ?? null)} />
            </Button>
            {form.photo ? <Button color="inherit" onClick={() => update('photo', null)}>{t('catalog.clearPhoto')}</Button> : null}
            {form.photo ? <Typography variant="caption" color="warning.main">{t('catalog.notUploadedYet')}</Typography> : null}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Button variant="outlined" component="label">
              {t('catalog.galleryImages')}
              <input hidden multiple type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" onChange={(e) => update('images', [...form.images, ...Array.from(e.target.files ?? [])])} />
            </Button>
            {form.images.map((image, index) => (
              <Chip key={`${image.name}-${index}`} label={image.name} onDelete={() => update('images', form.images.filter((_, i) => i !== index))} />
            ))}
          </Stack>
          {fieldError(errors, 'images.0') ? <Typography variant="caption" color="error">{fieldError(errors, 'images.0')}</Typography> : null}
          <TextField size="small" label={t('catalog.videoUrl')} value={form.video} onChange={(e) => update('video', e.target.value)} error={Boolean(err('video'))} helperText={err('video') || t('catalog.videoTip')} />
          <TextField size="small" label={tLabel('Summary')} multiline minRows={2} value={form.summary} onChange={(e) => update('summary', e.target.value)} />
          <TextField size="small" label={tLabel('Details')} multiline minRows={3} value={form.details} onChange={(e) => update('details', e.target.value)} />
          <Stack direction="row" spacing={3}>
            <FormControlLabel control={<Switch checked={form.hide_in_pos} onChange={(e) => update('hide_in_pos', e.target.checked)} />} label={t('catalog.hideInPos')} />
            <FormControlLabel control={<Switch checked={form.hide_in_shop} onChange={(e) => update('hide_in_shop', e.target.checked)} />} label={t('catalog.hideInShop')} />
          </Stack>
        </Stack>
      </EditorSection>
    </Stack>
  );
}

type SetForm = React.Dispatch<React.SetStateAction<ItemForm>>;

function VariantsStep({ form, errors, setForm }: { form: ItemForm; errors: ValidationErrors; setForm: SetForm }) {
  const [variantName, setVariantName] = useState('');
  const [variantOptions, setVariantOptions] = useState('');
  const [variantError, setVariantError] = useState('');

  const addVariant = () => {
    const name = variantName.trim();
    if (!name) return setVariantError(t('catalog.validation.required', { field: t('catalog.variantName') }));
    if (form.variants.some((variant) => variant.name === name)) return setVariantError(t('catalog.variantExists'));
    const optionsList = variantOptions.split('|').map((option) => option.trim()).filter(Boolean);
    setForm((current) => {
      let variations = current.variations.length ? current.variations : [emptyVariation()];
      if (optionsList.length) {
        const extra: VariationRow[] = [];
        variations = variations.map((variation) => ({ ...variation, meta: { ...variation.meta, [name]: optionsList[0] } }));
        optionsList.slice(1).forEach((option) => {
          const row = emptyVariation();
          row.stock = current.stock.map((stock) => ({ ...stock, rack: '', cost: null, price: null, dirty: false }));
          row.meta = { ...Object.fromEntries(current.variants.map((variant) => [variant.name, ''])), [name]: option };
          extra.push(row);
        });
        variations = [...variations, ...extra];
      } else {
        variations = variations.map((variation) => ({ ...variation, meta: { ...variation.meta, [name]: '' } }));
      }
      return { ...current, variants: [...current.variants, { name, options: optionsList }], variations };
    });
    setVariantName('');
    setVariantOptions('');
    setVariantError('');
  };

  const removeVariant = (name: string) => {
    setForm((current) => ({
      ...current,
      variants: current.variants.filter((variant) => variant.name !== name),
      variations: current.variations.map((variation) => {
        const meta = { ...variation.meta };
        delete meta[name];
        return { ...variation, meta };
      })
    }));
  };

  const generateAll = () => {
    if (form.variants.length < 1) return;
    const combos = form.variants.reduce<Array<Record<string, string>>>((acc, variant) => {
      const values = variant.options.length ? variant.options : [''];
      return acc.flatMap((meta) => values.map((value) => ({ ...meta, [variant.name]: value })));
    }, [{}]);
    setForm((current) => ({
      ...current,
      variations: combos.map((meta) => {
        const existing = current.variations.find((variation) => form.variants.every((variant) => variation.meta[variant.name] === meta[variant.name]));
        if (existing) return existing;
        const row = emptyVariation();
        row.meta = meta;
        row.stock = current.stock.map((stock) => ({ ...stock, rack: '', cost: null, price: null, dirty: false }));
        return row;
      })
    }));
  };

  const updateVariation = (index: number, patch: Partial<VariationRow>) => {
    setForm((current) => ({ ...current, variations: current.variations.map((variation, i) => (i === index ? { ...variation, ...patch } : variation)) }));
  };

  return (
    <Stack spacing={2}>
      <FormControlLabel control={<Checkbox checked={form.has_variants} onChange={(e) => setForm((current) => ({ ...current, has_variants: e.target.checked }))} />} label={t('catalog.hasVariants')} />
      {fieldError(errors, 'variants') ? <Alert severity="error">{fieldError(errors, 'variants')}</Alert> : null}
      {form.has_variants ? (
        <>
          <EditorSection title={t('catalog.variants')}>
            <Stack spacing={1.5}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {form.variants.map((variant) => (
                  <Chip key={variant.name} label={`${variant.name}: ${variant.options.join(' | ') || '—'}`} onDelete={() => removeVariant(variant.name)} />
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField size="small" label={t('catalog.variantName')} placeholder={t('catalog.variantNameTip')} value={variantName} onChange={(e) => setVariantName(e.target.value)} error={Boolean(variantError)} helperText={variantError} />
                <TextField size="small" sx={{ flex: 1 }} label={t('catalog.variantOptions')} placeholder={t('catalog.variantOptionsTip')} value={variantOptions} onChange={(e) => setVariantOptions(e.target.value)} />
                <Button variant="outlined" startIcon={<Add size={16} />} onClick={addVariant}>{t('catalog.addVariant')}</Button>
                <Button variant="outlined" color="warning" startIcon={<Shuffle size={16} />} onClick={generateAll} disabled={!form.variants.length}>{t('catalog.generateVariations')}</Button>
              </Stack>
            </Stack>
          </EditorSection>
          <EditorSection
            title={t('catalog.variations')}
            action={<Button size="small" startIcon={<Add size={15} />} onClick={() => setForm((current) => ({ ...current, variations: [...current.variations, { ...emptyVariation(), meta: Object.fromEntries(current.variants.map((variant) => [variant.name, ''])), stock: current.stock.map((stock) => ({ ...stock, rack: '', cost: null, price: null, dirty: false })) }] }))}>{t('catalog.addRow')}</Button>}
          >
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('catalog.sku')}</TableCell>
                    <TableCell>{tLabel('Code')}</TableCell>
                    {form.variants.map((variant) => <TableCell key={variant.name}>{variant.name}</TableCell>)}
                    <TableCell>{tLabel('Cost')}</TableCell>
                    <TableCell>{tLabel('Price')}</TableCell>
                    <TableCell>{t('catalog.rack')}</TableCell>
                    <TableCell>{t('catalog.weight')}</TableCell>
                    <TableCell>{t('catalog.dimensions')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.variations.map((variation, index) => (
                    <TableRow key={variation.rowKey}>
                      <TableCell sx={{ minWidth: 150 }}><Typography variant="caption">{variation.sku}</Typography></TableCell>
                      <TableCell sx={{ minWidth: 120 }}><TextField size="small" value={variation.code} onChange={(e) => updateVariation(index, { code: e.target.value })} /></TableCell>
                      {form.variants.map((variant) => (
                        <TableCell key={variant.name} sx={{ minWidth: 130 }}>
                          {variant.options.length ? (
                            <TextField select size="small" fullWidth value={variation.meta[variant.name] ?? ''} onChange={(e) => updateVariation(index, { meta: { ...variation.meta, [variant.name]: e.target.value } })}>
                              <MenuItem value="">—</MenuItem>
                              {variant.options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                            </TextField>
                          ) : (
                            <TextField size="small" value={variation.meta[variant.name] ?? ''} onChange={(e) => updateVariation(index, { meta: { ...variation.meta, [variant.name]: e.target.value } })} />
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ minWidth: 110 }}><TextField size="small" type="number" value={numberInput(variation.cost)} onChange={(e) => updateVariation(index, { cost: parseNum(e.target.value) })} /></TableCell>
                      <TableCell sx={{ minWidth: 110 }}><TextField size="small" type="number" value={numberInput(variation.price)} onChange={(e) => updateVariation(index, { price: parseNum(e.target.value) })} /></TableCell>
                      <TableCell sx={{ minWidth: 100 }}><TextField size="small" value={variation.rack} onChange={(e) => updateVariation(index, { rack: e.target.value })} /></TableCell>
                      <TableCell sx={{ minWidth: 90 }}><TextField size="small" type="number" value={numberInput(variation.weight)} onChange={(e) => updateVariation(index, { weight: parseNum(e.target.value) })} /></TableCell>
                      <TableCell sx={{ minWidth: 130 }}><TextField size="small" placeholder={t('catalog.dimensionsTip')} value={variation.dimensions} onChange={(e) => updateVariation(index, { dimensions: e.target.value })} /></TableCell>
                      <TableCell>
                        <Tooltip title={t('action.delete')}>
                          <IconButton size="small" color="error" onClick={() => setForm((current) => ({ ...current, variations: current.variations.filter((_, i) => i !== index) }))}><Trash size={16} /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {form.variations.some((variation) => variation.id) ? <Alert severity="warning" sx={{ mt: 1 }}>{t('catalog.variationRemovalWarning')}</Alert> : null}
          </EditorSection>
        </>
      ) : null}
    </Stack>
  );
}

function LocationsStep({ form, locations, setForm }: { form: ItemForm; locations: Array<{ value: string; label: string }>; setForm: SetForm }) {
  const current = useSessionLocation();
  const updateStock = (locationId: string, patch: Partial<StockRow>) => {
    setForm((state) => ({ ...state, stock: state.stock.map((row) => (row.location_id === locationId ? { ...row, ...patch, dirty: true } : row)) }));
  };
  const updateVariationStock = (variationIndex: number, locationId: string, patch: Partial<StockRow>) => {
    setForm((state) => ({
      ...state,
      variations: state.variations.map((variation, i) => (i === variationIndex ? { ...variation, stock: variation.stock.map((row) => (row.location_id === locationId ? { ...row, ...patch, dirty: true } : row)) } : variation))
    }));
  };
  return (
    <Stack spacing={2}>
      <FormControlLabel control={<Checkbox checked={form.is_stock} onChange={(e) => setForm((state) => ({ ...state, is_stock: e.target.checked }))} />} label={t('catalog.differentPerLocation')} />
      <Alert severity="info">{t('catalog.locationStockInfo')}</Alert>
      <EditorSection title={t('catalog.alertQuantity')} description={t('catalog.alertQuantityHelp')}>
        <Grid2>
          {locations.map((location) => {
            const row = form.stock.find((entry) => entry.location_id === location.value);
            if (!row) return null;
            return (
              <TextField
                key={location.value}
                size="small"
                type="number"
                label={locations.length > 1 ? `${t('catalog.alertQuantityShort')} · ${location.label}` : t('catalog.alertQuantityShort')}
                value={numberInput(row.alert_quantity ?? null)}
                inputProps={{ min: 0, step: 'any' }}
                onChange={(e) => {
                  const value = parseNum(e.target.value);
                  updateStock(location.value, { alert_quantity: typeof value === 'number' && value < 0 ? 0 : value });
                }}
              />
            );
          })}
        </Grid2>
      </EditorSection>
      {form.is_stock
        ? locations.map((location) => {
            const row = form.stock.find((entry) => entry.location_id === location.value);
            if (!row) return null;
            return (
              <EditorSection key={location.value} title={location.label} description={current.currentId === location.value ? t('catalog.currentLocation') : undefined}>
                {form.has_variants ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('catalog.variation')}</TableCell>
                          <TableCell>{tLabel('Cost')}</TableCell>
                          <TableCell>{tLabel('Price')}</TableCell>
                          <TableCell>{t('catalog.rack')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {form.variations.map((variation, index) => {
                          const stock = variation.stock.find((entry) => entry.location_id === location.value);
                          if (!stock) return null;
                          return (
                            <TableRow key={variation.rowKey}>
                              <TableCell>{Object.values(variation.meta).filter(Boolean).join(' / ') || variation.sku}</TableCell>
                              <TableCell><TextField size="small" type="number" value={numberInput(stock.cost)} onChange={(e) => updateVariationStock(index, location.value, { cost: parseNum(e.target.value) })} /></TableCell>
                              <TableCell><TextField size="small" type="number" value={numberInput(stock.price)} onChange={(e) => updateVariationStock(index, location.value, { price: parseNum(e.target.value) })} /></TableCell>
                              <TableCell><TextField size="small" value={stock.rack} onChange={(e) => updateVariationStock(index, location.value, { rack: e.target.value })} /></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Grid2>
                    <TextField size="small" type="number" label={tLabel('Cost')} value={numberInput(row.cost)} onChange={(e) => updateStock(location.value, { cost: parseNum(e.target.value) })} />
                    <TextField size="small" type="number" label={tLabel('Price')} value={numberInput(row.price)} onChange={(e) => updateStock(location.value, { price: parseNum(e.target.value) })} />
                    <TextField size="small" label={t('catalog.rack')} value={row.rack} onChange={(e) => updateStock(location.value, { rack: e.target.value })} />
                  </Grid2>
                )}
              </EditorSection>
            );
          })
        : null}
    </Stack>
  );
}

function serialCount(number: string, till: string) {
  if (!number.trim()) return 0;
  if (!till.trim()) return 1;
  const a = Number(number);
  const z = Number(till);
  if (!Number.isFinite(a) || !Number.isFinite(z)) return 1;
  return z >= a ? z - a + 1 : 0;
}

function SerialsStep({ form, errors, setForm }: { form: ItemForm; errors: ValidationErrors; setForm: SetForm }) {
  const total = form.serials.reduce((sum, serial) => sum + serialCount(serial.number, serial.till), 0);
  const updateSerial = (index: number, patch: Partial<{ number: string; till: string }>) => {
    setForm((state) => {
      const serials = state.serials.map((serial, i) => (i === index ? { ...serial, ...patch } : serial));
      if (index === serials.length - 1 && serials[index].number.trim()) serials.push({ number: '', till: '' });
      return { ...state, serials };
    });
  };
  return (
    <Stack spacing={2}>
      <FormControlLabel control={<Checkbox checked={form.has_serials} onChange={(e) => setForm((state) => ({ ...state, has_serials: e.target.checked }))} />} label={t('catalog.hasSerials')} />
      {fieldError(errors, 'serials') ? <Alert severity="error">{fieldError(errors, 'serials')}</Alert> : null}
      {form.has_serials ? (
        <EditorSection title={t('catalog.serialNumbers')} description={t('catalog.serialsProvided', { count: total })}>
          <Stack spacing={1} divider={<Divider flexItem />}>
            {form.serials.map((serial, index) => (
              <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField size="small" label={t('catalog.serialNumber')} value={serial.number} onChange={(e) => updateSerial(index, { number: e.target.value })} />
                <TextField size="small" label={t('catalog.till')} value={serial.till} onChange={(e) => updateSerial(index, { till: e.target.value })} />
                <Typography variant="body2" sx={{ minWidth: 120 }}>{serial.number ? t('catalog.serialsCount', { count: serialCount(serial.number, serial.till) }) : ''}</Typography>
                {form.serials.length > 1 && index < form.serials.length - 1 ? (
                  <IconButton size="small" color="error" onClick={() => setForm((state) => ({ ...state, serials: state.serials.filter((_, i) => i !== index) }))}><Trash size={16} /></IconButton>
                ) : null}
              </Stack>
            ))}
          </Stack>
        </EditorSection>
      ) : null}
    </Stack>
  );
}

function PortionsStep({ form, errors, setForm }: { form: ItemForm; errors: ValidationErrors; setForm: SetForm }) {
  const [newPortion, setNewPortion] = useState('');
  const regular = useMemo(() => regularPortion(form.portions), [form.portions]);

  const addLine = () => {
    const line: RecipeLine = { rowKey: rowKey('line'), item: null, cost: 0 };
    setForm((state) => ({ ...state, recipeLines: [...state.recipeLines, line], portions: state.portions.map((portion) => ({ ...portion, quantities: { ...portion.quantities, [line.rowKey]: 1 } })) }));
  };
  const removeLine = (key: string) => {
    setForm((state) => ({ ...state, recipeLines: state.recipeLines.filter((line) => line.rowKey !== key) }));
  };
  const setLineItem = (key: string, option: RemoteOption | null) => {
    setForm((state) => ({ ...state, recipeLines: state.recipeLines.map((line) => (line.rowKey === key ? { ...line, item: option, cost: numOr0(option?.raw?.cost) } : line)) }));
  };
  const updatePortion = (index: number, patch: Partial<RecipePortion>) => {
    setForm((state) => ({ ...state, portions: state.portions.map((portion, i) => (i === index ? { ...portion, ...patch } : portion)) }));
  };
  const addPortion = () => {
    const name = newPortion.trim();
    if (!name || form.portions.some((portion) => portion.name === name)) return;
    setForm((state) => ({ ...state, portions: [...state.portions, { ...emptyPortion(name, state.recipeLines), quantities: { ...(regular?.quantities ?? {}) } }] }));
    setNewPortion('');
  };

  return (
    <Stack spacing={2}>
      {fieldError(errors, 'portions') ? <Alert severity="error">{fieldError(errors, 'portions')}</Alert> : null}
      <EditorSection title={t('catalog.recipeItems')} description={t('catalog.recipeItemsTip')} action={<Button size="small" startIcon={<Add size={15} />} onClick={addLine}>{t('catalog.addItem')}</Button>}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 260 }}>{tLabel('Item')}</TableCell>
                <TableCell>{tLabel('Cost')}</TableCell>
                {form.portions.map((portion, index) => (
                  <TableCell key={portion.rowKey} sx={{ minWidth: 120 }}>
                    {t('catalog.qtyIn', { portion: portion.name === 'regular' ? t('catalog.regular') : portion.name })}
                    {fieldError(errors, `portions.${index}.items`) ? <Typography variant="caption" color="error" display="block">{fieldError(errors, `portions.${index}.items`)}</Typography> : null}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.recipeLines.map((line) => (
                <TableRow key={line.rowKey}>
                  <TableCell>
                    <RemoteAutocomplete endpoint="items/search" params={{ cost: 'yes' }} requiresLocation value={line.item} onChange={(option) => setLineItem(line.rowKey, option)} />
                  </TableCell>
                  <TableCell>{line.cost.toFixed(2)}</TableCell>
                  {form.portions.map((portion, index) => (
                    <TableCell key={portion.rowKey}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        value={numberInput(portion.quantities[line.rowKey] ?? '')}
                        onChange={(e) => updatePortion(index, { quantities: { ...portion.quantities, [line.rowKey]: parseNum(e.target.value) } })}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => removeLine(line.rowKey)} disabled={form.recipeLines.length <= 1}><Trash size={16} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </EditorSection>

      <EditorSection
        title={t('catalog.portions')}
        action={
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder={t('catalog.portionName')} value={newPortion} onChange={(e) => setNewPortion(e.target.value)} />
            <Button size="small" variant="outlined" startIcon={<Add size={15} />} onClick={addPortion}>{t('catalog.addPortion')}</Button>
          </Stack>
        }
      >
        <Stack spacing={1.5}>
          {form.portions.map((portion, index) => {
            const computed = recipePortionCost(portion, form.recipeLines);
            const isRegular = portion.name.trim().toLowerCase() === 'regular';
            return (
              <Stack key={portion.rowKey} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
                <TextField size="small" label={tLabel('Name')} value={isRegular ? t('catalog.regular') : portion.name} disabled={isRegular} onChange={(e) => updatePortion(index, { name: e.target.value })} error={Boolean(fieldError(errors, `portions.${index}.name`))} helperText={fieldError(errors, `portions.${index}.name`)} />
                <TextField
                  size="small"
                  type="number"
                  label={tLabel('Cost')}
                  value={numberInput(portion.cost)}
                  onChange={(e) => updatePortion(index, { cost: parseNum(e.target.value) })}
                  error={Boolean(fieldError(errors, `portions.${index}.cost`))}
                  helperText={fieldError(errors, `portions.${index}.cost`) || t('catalog.computedCost', { amount: computed.toFixed(2) })}
                />
                <Button size="small" onClick={() => updatePortion(index, { cost: Number(computed.toFixed(4)) })} sx={{ mt: 0.5 }}>{t('catalog.useComputed')}</Button>
                <TextField size="small" type="number" label={tLabel('Price')} value={numberInput(portion.price)} onChange={(e) => updatePortion(index, { price: parseNum(e.target.value) })} error={Boolean(fieldError(errors, `portions.${index}.price`))} helperText={fieldError(errors, `portions.${index}.price`)} />
                {!isRegular ? (
                  <IconButton color="error" onClick={() => setForm((state) => ({ ...state, portions: state.portions.filter((_, i) => i !== index) }))}><Trash size={16} /></IconButton>
                ) : null}
              </Stack>
            );
          })}
          <Typography variant="caption" color="text.secondary">{t('catalog.regularPortionTip')}</Typography>
        </Stack>
      </EditorSection>
    </Stack>
  );
}
